import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Eye, Loader2, ShieldOff, Mail, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { SHARE_FIELD_LABELS } from "@/lib/intakeShareFields";

const APP_URL = (import.meta.env.VITE_PUBLIC_APP_URL || "https://dashboard.creativeinvestments.africa").replace(/\/$/, "");

export function ActiveSharesCard({ leadId }: { leadId: string }) {
  const qc = useQueryClient();
  const [resendingId, setResendingId] = useState<string | null>(null);


  const sharesQ = useQuery({
    queryKey: ["intake-shares", leadId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("intake_share_links")
        .select("*")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const revokeMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from("intake_share_links")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["intake-shares", leadId] }); toast.success("Share link revoked"); },
    onError: (e: any) => toast.error(e?.message || "Failed to revoke"),
  });

  const resendMut = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.functions.invoke("send-intake-share-invite", {
        body: { shareId: id },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      return data as { sentTo?: string };
    },
    onSuccess: (data) => {
      setResendingId(null);
      qc.invalidateQueries({ queryKey: ["intake-shares", leadId] });
      toast.success(`Link emailed to ${data?.sentTo || "the recipient"}`);
    },
    onError: (e: any) => { setResendingId(null); toast.error(e?.message || "Failed to send email"); },
  });



  const shares = sharesQ.data || [];
  if (!sharesQ.isLoading && shares.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Shared with ({shares.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {sharesQ.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          shares.map((s: any) => {
            const expired = new Date(s.expires_at) < new Date();
            const revoked = !!s.revoked_at;
            const url = `${APP_URL}/intake-share/${s.token}`;
            return (
              <div key={s.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{s.recipient_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{s.recipient_email} · {s.purpose}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {revoked ? (
                      <Badge variant="destructive">Revoked</Badge>
                    ) : expired ? (
                      <Badge variant="secondary">Expired</Badge>
                    ) : (
                      <Badge variant="default">Active</Badge>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="secondary" className="text-[10px]">{(s.submission_ids || []).length} records</Badge>
                  {(s.shared_fields || []).slice(0, 5).map((f: string) => (
                    <Badge key={f} variant="outline" className="text-[10px]">{SHARE_FIELD_LABELS[f] || f}</Badge>
                  ))}
                  {(s.shared_fields || []).length > 5 && (
                    <Badge variant="outline" className="text-[10px]">+{(s.shared_fields || []).length - 5} more</Badge>
                  )}
                  {s.mask_passport && <Badge variant="outline" className="text-[10px]">passport masked</Badge>}
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {s.view_count} views</span>
                    <span>Expires {new Date(s.expires_at).toLocaleDateString()}</span>
                    {s.last_viewed_at && <span>Last viewed {new Date(s.last_viewed_at).toLocaleString()}</span>}
                    {s.invite_sent_at && (
                      <span className="flex items-center gap-1">
                        <MailCheck className="h-3 w-3" /> Emailed {new Date(s.invite_sent_at).toLocaleString()}
                        {s.invite_send_count > 1 ? ` (${s.invite_send_count}×)` : ""}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-1">
                    {!revoked && !expired && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={resendMut.isPending && resendingId === s.id}
                          onClick={() => { setResendingId(s.id); resendMut.mutate(s.id); }}
                        >
                          {resendMut.isPending && resendingId === s.id ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Mail className="h-3 w-3 mr-1" />
                          )}
                          {s.invite_sent_at ? "Resend email" : "Email link"}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }}>
                          <Copy className="h-3 w-3 mr-1" /> Copy link
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => revokeMut.mutate(s.id)}>
                          <ShieldOff className="h-3 w-3 mr-1" /> Revoke
                        </Button>
                      </>
                    )}
                  </span>

                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
